// @flow
import React, { useContext, useState } from 'react';
import { Formik } from 'formik';
import { useIsUserAdmin } from '@src/custom-hooks/useIsUserAdmin';

import { APPLY_SYSTEM_GENERATED_DISCOUNTS_BACKEND_NAME } from '../../components/common/vbd/constants';
import { StyledContainer } from '../../common-utils/styledComponents';
import { ESTIMATE_TOP_LEVEL_CONFIG } from '../../configuration/estimate-top-level-config';
import { Form } from '../../formik-utils/formikSUIWrapper';
import {
  getFormikFormInputsFromColConfigAndInputSource,
  doesEstimateHaveAnyResourcesConfigured,
} from '../../common-utils/utils';
import { EstimateContext } from '../../contexts/EstimateContext';
import { useEstimateMLDForFieldGuidance } from '../../custom-hooks/useEstimateMLDForFieldGuidance';
import { useAdvancedSecurityConfiguration } from '../../custom-hooks/useAdvancedSecurityConfiguration';
import {
  ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  ADVANCED_SECURITY_ENABLED_BACKEND_NAME,
  COMMITTED_CAPACITY_INPUTS_BACKEND_NAME,
  CREDITS_AND_BURSTS_JSON_BACKEND_NAME,
  CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  ESTIMATE_CUSTOMER_VIEW_ENABLED_BACKEND_NAME,
  ESTIMATE_CUSTOMER_VIEW_ENABLED_DISPLAY_NAME,
  GATEWAY_ADD_ON_ENABLED_BACKEND_NAME,
  IS_ADVANCED_SECURITY_LIVE_IN_CE1,
  IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
  IS_GATEWAY_ADD_ON_LIVE_IN_CE1,
  TCO_INPUTS_JSON_BACKEND_NAME,
} from '../../constants';
import { DeleteButton } from '../presentational/buttons/DeleteButton';
import { EstimateDeleteWarningModal } from '../home-page/hierarchy-view/estimates-grid/EstimateDeleteWarningModal';
import { CopyButton } from '../presentational/buttons/CopyButton';
import { toastError, toastSuccess } from '../presentational/notifications/utils';
import {
  useCopyEstimateMutation,
  useUpdateEstimateMutation,
} from '../../service-definitions/estimates';
import { getLinkForEstimate } from '../links/utils';
import { Spacer } from '../presentational/spacing/Spacer';
import { CheckBoxField } from '../../formik-utils/FormFields';
import { ShareButton } from '../presentational/buttons/ShareButton';
import { ImportBaselineButton } from '../presentational/buttons/ImportBaselineButton';
import { EstimateShareModal } from '../estimate-sharing/EstimateShareModal';
import { CE1BaselineImportModal } from '../ce1-baseline/ImportModal';
import { FedRAMPTagContainer } from '../fedramp/FedRAMPTagContainer';
import { If } from '../presentational/If';

import EstimateAccordionsContainer from './EstimateAccordionsContainer';

const EstimateTopLevelContainerForm = () => {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [isBaselineModalOpen, setBaselineModalOpen] = useState(false);
  const isUserAdmin = useIsUserAdmin();
  const estimateData = useContext(EstimateContext);
  const [copyEstimate] = useCopyEstimateMutation();
  const [updateEstimateDetails] = useUpdateEstimateMutation();
  const mldGuidance = useEstimateMLDForFieldGuidance();
  const advancedSecurityConfiguration = useAdvancedSecurityConfiguration();

  const estimateInputsSource = {
    ...estimateData.inputs,
    [CREDITS_AND_BURSTS_JSON_BACKEND_NAME]: {
      cnbc_configs: estimateData?.cnbc_inputs ?? [],
    },
    // IMPORTANT: When customer_view_enabled=true, backend omits metrics_discount from response.
    // We must explicitly set global_discount from inputs.global_discount to ensure the validation
    // schema receives the correct value. Without this, global_discount would be 0 (from empty object),
    // causing validation to fail for non-admin users (0% < 5% minimum), disabling the entire page.
    [CUSTOM_DISCOUNTS_JSON_BACKEND_NAME]: {
      ...estimateData?.metrics_discount,
      global_discount: estimateData?.inputs?.global_discount,
    },
    [TCO_INPUTS_JSON_BACKEND_NAME]: estimateData?.inputs?.[TCO_INPUTS_JSON_BACKEND_NAME] ?? {},
    ...(IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1 && {
      [COMMITTED_CAPACITY_INPUTS_BACKEND_NAME]: estimateData?.committed_capacity_inputs ?? [],
    }),
    net_new_customer: estimateData?.inputs?.net_new_customer ?? false,
    msk_takeout: estimateData?.inputs?.msk_takeout ?? false,
    [APPLY_SYSTEM_GENERATED_DISCOUNTS_BACKEND_NAME]:
      estimateData?.inputs?.[APPLY_SYSTEM_GENERATED_DISCOUNTS_BACKEND_NAME] ?? false,
    ...(IS_ADVANCED_SECURITY_LIVE_IN_CE1 && {
      [ADVANCED_SECURITY_ENABLED_BACKEND_NAME]:
        estimateData?.inputs?.[ADVANCED_SECURITY_ENABLED_BACKEND_NAME] ?? false,
      [ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME]: advancedSecurityConfiguration,
    }),
    ...(IS_GATEWAY_ADD_ON_LIVE_IN_CE1 && {
      [GATEWAY_ADD_ON_ENABLED_BACKEND_NAME]:
        estimateData?.inputs?.[GATEWAY_ADD_ON_ENABLED_BACKEND_NAME] ?? false,
    }),
  };

  const { initialValues, initialTouched, validationSchema } =
    getFormikFormInputsFromColConfigAndInputSource(
      ESTIMATE_TOP_LEVEL_CONFIG,
      estimateInputsSource,
      {
        ...estimateData,
        isUserAdmin,
        mldGuidance,
      }
    );

  return (
    <StyledContainer>
      <Formik
        enableReinitialize={true}
        initialTouched={initialTouched}
        initialValues={initialValues}
        onSubmit={() => {}}
        validateOnMount={true}
        validationSchema={validationSchema}
      >
        {() => (
          <Form autoComplete="off">
            <DeleteButton
              onClick={() => {
                setDeleteModalOpen(true);
              }}
              size="large"
            />
            <CopyButton
              onClick={async () => {
                const { error, data } = await copyEstimate({
                  estimateId: estimateInputsSource.id,
                });
                if (error) {
                  toastError(error);
                } else {
                  toastSuccess(
                    <>
                      Estimate copied successfully. You can open it by clicking <Spacer x={5} />
                      <a href={getLinkForEstimate(data?.id)}>
                        <strong>here!!!</strong>
                      </a>
                    </>,
                    false
                  );
                }
              }}
            />
            <ShareButton
              onClick={() => {
                setShareModalOpen(true);
              }}
              size="large"
            />
            <ImportBaselineButton
              disabled={doesEstimateHaveAnyResourcesConfigured(estimateData)}
              onClick={() => setBaselineModalOpen(true)}
              size="large"
            />
            <CheckBoxField
              fieldDisplayName={ESTIMATE_CUSTOMER_VIEW_ENABLED_DISPLAY_NAME}
              fieldName={ESTIMATE_CUSTOMER_VIEW_ENABLED_BACKEND_NAME}
              onChange={async (event, value, setFieldValue, setFieldTouched, values, checked) => {
                const payload = {
                  [ESTIMATE_CUSTOMER_VIEW_ENABLED_BACKEND_NAME]: checked,
                  estimate_version: estimateInputsSource?.estimate_version,
                };

                const { error } = await updateEstimateDetails({
                  estimateId: estimateInputsSource?.id,
                  payload,
                });

                if (error) {
                  toastError(error);
                }
              }}
            />
            <FedRAMPTagContainer />
            <EstimateDeleteWarningModal
              estimateId={estimateInputsSource.id}
              estimateName={estimateInputsSource.name}
              estimateVersion={estimateInputsSource.estimate_version}
              isOpen={isDeleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
            />
            <EstimateShareModal
              estimateId={estimateInputsSource.id}
              estimateName={estimateInputsSource.name}
              estimateVersion={estimateInputsSource.estimate_version}
              isOpen={isShareModalOpen}
              setOpen={setShareModalOpen}
            />
            <If test={isBaselineModalOpen}>
              <CE1BaselineImportModal isOpen={isBaselineModalOpen} setOpen={setBaselineModalOpen} />
            </If>
            <Spacer y={10} />
            <EstimateAccordionsContainer />
          </Form>
        )}
      </Formik>
    </StyledContainer>
  );
};

export default EstimateTopLevelContainerForm;
